import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useBranding } from "@/contexts/BrandingContext";
import { useSiteAsset } from "@/lib/useSiteAsset";
import { LanguageSelector } from "@/components/LanguageSelector";
import { TreePine, ShieldCheck } from "lucide-react";

const HERO_IMG =
    "https://static.prod-images.emergentagent.com/jobs/3d3352ae-c0ac-4bb6-8151-8508cd0f0313/images/b9e9f5737ea75fbf658548a624210866d9fdb2a5cec26cd570d35464e695562c.jpeg";
const HERO_FALLBACK =
    "https://res.cloudinary.com/hpnhnzfl/image/upload/banners/ai_login_hero.jpg";

function onImgError(e) {
    if (e.currentTarget.src !== HERO_FALLBACK) {
        e.currentTarget.onerror = null;
        e.currentTarget.src = HERO_FALLBACK;
    }
}

function BrandMark({ app_name, logo, onClick, light = false }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={`Go to ${app_name} homepage`}
            className="inline-flex items-center gap-2.5"
            data-testid="auth-brand"
        >
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-[#1B5E20] overflow-hidden shadow-sm">
                {logo ? (
                    <img src={logo} alt={app_name} className="h-10 w-10 object-cover" />
                ) : (
                    <svg viewBox="0 0 24 24" className="h-5 w-5 text-white" fill="none" stroke="currentColor" strokeWidth="1.7">
                        <path d="M12 3v18M5 9c0-3 3-5 7-5s7 2 7 5-3 5-7 5-7-2-7-5Z" />
                    </svg>
                )}
            </span>
            <span className={`text-[19px] font-bold ${light ? "text-white" : "text-[#1B5E20]"}`}>{app_name}</span>
        </button>
    );
}

/**
 * Premium split-screen auth layout (matches Login.pdf).
 * Left  : full-bleed family photo + warm green overlay + heading/supporting text.
 * Right : light background with a centered white auth card (children).
 * Mobile: single clean column (image hidden), branding preserved.
 */
export function AuthSplitLayout({ heroHeading, heroText, testIdPrefix = "auth", children }) {
    const navigate = useNavigate();
    const { app_name, logo } = useBranding();
    // Admin → Site Assets → "Login/Signup Page — Side Illustration" overrides the bundled photo.
    const sideAsset = useSiteAsset(testIdPrefix === "signup" ? "marketing.signup_side" : "marketing.login_side", null);
    const heroSrc = sideAsset.url || HERO_IMG;

    return (
        <div className="min-h-[100svh] md:h-[100svh] md:overflow-hidden w-full flex bg-[#FDFBF5] overflow-x-hidden" data-testid={`${testIdPrefix}-page`} data-auth-compact>
            {/* LEFT — hero (desktop/tablet) */}
            <aside className="relative hidden md:flex md:w-[52%] lg:w-[58%] xl:w-[62%] flex-col justify-between overflow-hidden md:h-[100svh]" data-testid={`${testIdPrefix}-hero`}>
                <img src={heroSrc} onError={onImgError} alt={sideAsset.alt || "A family sharing memories together across generations"} className="absolute inset-0 h-full w-full object-cover object-center" loading="eager" />
                {/* warm green overlay for legibility */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#0f3311]/72 via-[#123d15]/55 to-[#1B5E20]/45" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0d2b0f]/80 via-transparent to-[#0d2b0f]/25" />

                {/* top logo */}
                <div className="relative z-10 p-8 lg:p-10">
                    <BrandMark app_name={app_name} logo={logo} light onClick={() => navigate("/")} />
                </div>

                {/* headings */}
                <div className="relative z-10 p-8 lg:p-12 xl:p-16 max-w-2xl">
                    <motion.h2
                        initial={{ opacity: 0, y: 22 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-white font-bold leading-[1.06] text-[34px] lg:text-[44px] xl:text-[52px]"
                    >
                        {heroHeading}
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.15 }}
                        className="mt-5 text-white/90 text-[15px] lg:text-[17px] leading-relaxed max-w-xl"
                    >
                        {heroText}
                    </motion.p>
                </div>
            </aside>

            {/* RIGHT — auth card */}
            <main className="relative flex-1 md:w-[48%] lg:w-[42%] xl:w-[38%] flex flex-col min-h-[100svh] md:min-h-0 md:h-[100svh]">
                {/* top bar: mobile logo + language selector */}
                <div className="flex items-center justify-between px-4 sm:px-8 pt-2 md:pt-3">
                    <div className="md:hidden">
                        <BrandMark app_name={app_name} logo={logo} onClick={() => navigate("/")} />
                    </div>
                    <div className="hidden md:block" />
                    <LanguageSelector />
                </div>

                <div className="flex-1 min-h-0 flex items-center justify-center px-4 sm:px-8 lg:px-10 py-2 md:py-3">
                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                        className="w-full max-w-[440px] auth-card-scale"
                    >
                        <div className="relative rounded-[24px] bg-white shadow-[0_24px_60px_rgba(27,94,32,0.10)] border border-[#EFEADC] px-5 sm:px-7 py-5">
                            {/* card logo (top-center, matches PDF) */}
                            <div className="hidden md:flex justify-center mb-3">
                                <BrandMark app_name={app_name} logo={logo} onClick={() => navigate("/")} />
                            </div>
                            {children}
                        </div>

                        {/* Trust & security certifications — single compact row */}
                        <div className="mt-2 hidden md:block" data-testid="auth-trust">
                            <div className="flex flex-nowrap justify-center gap-1 overflow-hidden">
                                {["ISO 27001", "DPDP Act", "SOC 2", "Bank-level security"].map((b) => (
                                    <span key={b} className="inline-flex items-center gap-1 rounded-full border border-[#E3DFCF] bg-white/70 px-2 py-0.5 text-[10px] font-medium text-[#3E5540] whitespace-nowrap">
                                        <ShieldCheck className="h-3 w-3 text-[#1B5E20]" /> {b}
                                    </span>
                                ))}
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] text-[#7A8A7C] whitespace-nowrap"><TreePine className="h-3 w-3" /> Made in India</span>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </main>
        </div>
    );
}
